import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * LandingFAQ — 5 objections frontales.
 * Chaque réponse évite les promesses non tenables (pas de délai chiffré
 * garanti) et renvoie vers la réalité vérifiable du produit.
 */
const ITEMS = [
  {
    q: "Pourquoi payer alors que Duolingo, HelloTalk ou ChatGPT sont gratuits ?",
    a: (
      <>
        <p>
          Les apps gratuites sont excellentes pour <em>découvrir</em> une langue.
          Elles ne remplacent pas un parcours structuré pensé pour l'examen JLPT
          et rédigé en français. Concrètement, en payant vous avez : un chemin
          linéaire N5→N3 sans à-trous, des examens blancs chronométrés,
          de l'audio pour l'écoute et un tuteur qui se souvient
          de vos erreurs pour vous les faire réviser au bon moment.
        </p>
        <p className="mt-2">
          Beaucoup de nos élèves ont essayé Duolingo <em>avant</em> d'arriver ici.
        </p>
      </>
    ),
  },
  {
    q: "Est-ce vraiment adapté à un débutant qui n'a jamais fait de japonais ?",
    a: (
      <p>
        Oui, et c'est la population que nous ciblons en priorité. Vous commencez
        par les kana, hiragana puis katakana, en accès libre. En parallèle,
        chaque leçon du parcours introduit un concept et le fait pratiquer
        immédiatement. Aucun prérequis. Un{" "}
        <Link to="/placement" className="underline font-bold hover:text-[#E94A3F]">test de niveau gratuit</Link>{" "}
        vous confirme votre point de départ en 2 minutes.
      </p>
    ),
  },
  {
    q: "Combien de temps pour être prêt au JLPT N5 ?",
    a: (
      <p>
        Cela dépend de votre régularité, pas de nous. Le programme officiel du
        JLPT N5 couvre environ 100 kanji et 800 mots. C'est du travail réel,
        pas un slogan. Avec 15 minutes par jour et un rythme constant, la
        majorité des étudiants s'y sentent prêts en quelques mois. Nous ne
        promettons pas de délai chiffré : nous fournissons le parcours, la
        régularité vient de vous.
      </p>
    ),
  },
  {
    q: "Puis-je annuler ou me faire rembourser si ça ne me convient pas ?",
    a: (
      <p>
        Les 3 premiers jours sont gratuits sans carte bancaire, vous testez l'intégralité
        de la plateforme avant de décider. Après paiement, si vous changez d'avis dans
        les 14 jours, écrivez-nous à{" "}
        <a href="mailto:alexis.gresoviac@gmail.com" className="underline font-bold hover:text-[#E94A3F]">
          alexis.gresoviac@gmail.com
        </a>{" "}
        et nous vous remboursons, sans vous demander de vous justifier. Pour annuler
        un abonnement, il suffit également de nous écrire à cette adresse.
      </p>
    ),
  },
  {
    q: "Le tuteur IA remplace-t-il un vrai professeur ?",
    a: (
      <p>
        Non. Le tuteur IA est disponible 24/7 pour répondre à vos questions
        pendant votre étude (grammaire, kanji, prononciation, traduction).
        C'est un compagnon d'entraînement, pas un cours particulier. Il
        excelle pour débloquer un doute sur une particule à 23h ; il ne
        remplace pas un professeur pour la conversation orale, et nous le
        disons clairement.
      </p>
    ),
  },
];

function AccordionItem({ item, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="border-b border-[#1A2A40]/10" data-testid={`faq-item-${index}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-4 py-6 text-left group"
        aria-expanded={open}
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-display font-black text-lg sm:text-xl text-[#1A2A40] leading-snug pr-2">
          {item.q}
        </span>
        <ChevronDown
          className={`w-5 h-5 shrink-0 text-[#E94A3F] transition-transform mt-1 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all ${open ? "max-h-96 pb-6" : "max-h-0"}`}
      >
        <div className="text-[#4A5568] leading-relaxed max-w-3xl">{item.a}</div>
      </div>
    </div>
  );
}

export function LandingFAQ() {
  return (
    <section className="max-w-4xl mx-auto px-6 sm:px-12 py-24" data-testid="landing-faq-section">
      <div className="mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">Questions fréquentes</div>
        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1A2A40]">
          Les objections qu'on nous pose, <span className="italic font-light">honnêtement traitées.</span>
        </h2>
      </div>
      <div>
        {ITEMS.map((it, i) => <AccordionItem key={i} item={it} index={i} />)}
      </div>
    </section>
  );
}

export default LandingFAQ;
